import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Download, Hourglass, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { KpiLabel, KpiValue, KpiSub, ExportBtn, Skeleton } from '../styles';
import { KPI_ACCENTS, formatTry } from '../constants';
import { exportCardPng } from '../exportCardPng';

const KpiGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  position: relative;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  border-left: 4px solid ${({ $accent }) => $accent};
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 96px;
`;

const TileExport = styled(ExportBtn)`
  position: absolute;
  top: 8px;
  right: 8px;
`;

function pctDelta(current, prev) {
  if (!prev) return null;
  return Math.round(((current - prev) / prev) * 100);
}

function KpiTile({ cardKey, accent, label, value, sub, loading }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const handleExport = useCallback(() => {
    exportCardPng(ref.current, cardKey).catch(() => {});
  }, [cardKey]);

  return (
    <Tile ref={ref} $accent={accent}>
      <TileExport
        type="button"
        data-export-exclude
        onClick={handleExport}
        title={t('expenseDashboard.export.saveAsPng')}
        aria-label={t('expenseDashboard.export.saveAsPng')}
      >
        <Download size={13} />
      </TileExport>
      <KpiLabel>{label}</KpiLabel>
      {loading ? <Skeleton $h="42px" /> : (
        <>
          <KpiValue>{value}</KpiValue>
          <KpiSub>{sub}</KpiSub>
        </>
      )}
    </Tile>
  );
}

export default function KpiRow({ summary, risks }) {
  const { t } = useTranslation();
  const s = summary.data;
  const r = risks.data;

  const totalDelta = s ? pctDelta(s.approvedTotal, s.prevApprovedTotal) : null;
  const countDelta = s ? s.approvedCount - s.prevApprovedCount : 0;
  const TotalIcon = totalDelta > 0 ? ArrowUp : totalDelta < 0 ? ArrowDown : Minus;
  const CountIcon = countDelta > 0 ? ArrowUp : countDelta < 0 ? ArrowDown : Minus;

  return (
    <KpiGrid>
      <KpiTile
        cardKey="kpi-toplam-masraf"
        accent={KPI_ACCENTS.total}
        loading={summary.loading}
        label={t('expenseDashboard.kpi.totalExpense')}
        value={formatTry(s?.approvedTotal)}
        sub={(
          <>
            <TotalIcon size={12} />
            {totalDelta === null
              ? t('expenseDashboard.kpi.noPrevMonth')
              : t('expenseDashboard.kpi.vsPrevMonth', { delta: `${totalDelta > 0 ? '+' : ''}%${Math.abs(totalDelta)}` })}
            {s?.nonTryCount > 0 && ` · ${t('expenseDashboard.fxExcluded', { count: s.nonTryCount })}`}
          </>
        )}
      />
      <KpiTile
        cardKey="kpi-islem-adedi"
        accent={KPI_ACCENTS.count}
        loading={summary.loading}
        label={t('expenseDashboard.kpi.transactionCount')}
        value={s?.approvedCount ?? 0}
        sub={(
          <>
            <CountIcon size={12} />
            {t('expenseDashboard.kpi.countVsPrev', { delta: `${countDelta > 0 ? '+' : ''}${countDelta}` })}
          </>
        )}
      />
      <KpiTile
        cardKey="kpi-onay-bekleyen"
        accent={KPI_ACCENTS.pending}
        loading={summary.loading}
        label={t('expenseDashboard.kpi.pendingVolume')}
        value={formatTry(s?.pendingTotal)}
        sub={(
          <>
            <Hourglass size={12} />
            {t('expenseDashboard.kpi.pendingCount', { count: s?.pendingCount ?? 0 })}
          </>
        )}
      />
      <KpiTile
        cardKey="kpi-politika-ihlali"
        accent={KPI_ACCENTS.violation}
        loading={summary.loading && risks.loading}
        label={t('expenseDashboard.kpi.policyViolation')}
        value={s?.violationCount ?? r?.violationCount ?? 0}
        sub={t('expenseDashboard.kpi.newThisWeek', { count: s?.violationNewThisWeek ?? 0 })}
      />
    </KpiGrid>
  );
}
